let youtubePlayerApiPromise = null;

const queryAll = (selector, context = document) =>
	Array.from(context.querySelectorAll(selector));

const loadYoutubePlayerApi = () => {
	if (window.YT?.Player) {
		return Promise.resolve(window.YT);
	}

	if (youtubePlayerApiPromise) {
		return youtubePlayerApiPromise;
	}

	youtubePlayerApiPromise = new Promise((resolve, reject) => {
		const previousReadyCallback = window.onYouTubeIframeAPIReady;
		const timeoutId = window.setTimeout(() => {
			reject(new Error('YouTube IFrame API timed out.'));
		}, 10000);

		window.onYouTubeIframeAPIReady = () => {
			if (typeof previousReadyCallback === 'function') {
				previousReadyCallback();
			}

			window.clearTimeout(timeoutId);
			resolve(window.YT);
		};

		const existingScript = document.getElementById('jw-youtube-iframe-api');

		if (existingScript) {
			existingScript.addEventListener(
				'error',
				() => {
					window.clearTimeout(timeoutId);
					reject(new Error('Unable to load YouTube IFrame API.'));
				},
				{ once: true }
			);
			return;
		}

		const script = document.createElement('script');
		script.id = 'jw-youtube-iframe-api';
		script.src = 'https://www.youtube.com/iframe_api';
		script.async = true;
		script.onerror = () => {
			window.clearTimeout(timeoutId);
			reject(new Error('Unable to load YouTube IFrame API.'));
		};
		document.head.appendChild(script);
	});
	youtubePlayerApiPromise = youtubePlayerApiPromise.catch((error) => {
		youtubePlayerApiPromise = null;
		throw error;
	});

	return youtubePlayerApiPromise;
};

class HomeVideoController {
	constructor(root) {
		this.root = root;
		this.videos = [];
		this.activeIndex = 0;
		this.player = null;
		this.playerType = '';
		this.playerPromise = null;
		this.playerRequestId = 0;
		this.isVisible = false;
		this.lastFocusedElement = null;
		this.mobileMedia = window.matchMedia('(max-width: 575.98px)');
		this.reduceMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches;
		this.stage = root.querySelector('[data-video-stage]');
		this.playerContainer = root.querySelector('[data-video-player]');
		this.poster = root.querySelector('.jw-home-video__poster');
		this.playButton = root.querySelector('[data-video-play]');
		this.closeButton = root.querySelector('[data-video-modal-close]');
		this.title = root.querySelector('[data-video-title]');
		this.description = root.querySelector('[data-video-description]');
		this.views = root.querySelector('[data-video-views]');
		this.duration = root.querySelector('[data-video-duration]');
		this.published = root.querySelector('[data-video-published]');
		this.cards = queryAll('[data-video-card]', root);
		this.cardPreviews = queryAll('[data-video-card-preview]', root);
	}

	init() {
		const dataElement = this.root.querySelector('.jw-home-video__data');

		try {
			this.videos = JSON.parse(dataElement?.textContent || '[]');
		} catch (error) {
			this.videos = [];
		}

		if (
			!this.videos.length ||
			!this.stage ||
			!this.playerContainer ||
			!this.playButton
		) {
			return;
		}

		this.root.classList.add('is-ready');
		this.syncResponsiveState();
		this.initCardPreviews();

		this.playButton.addEventListener('click', () => {
			this.ensurePlayer(true);
		});

		this.cards.forEach((card) => {
			card.addEventListener('click', () => {
				const index = Number.parseInt(
					card.dataset.videoIndex || '',
					10
				);

				if (Number.isNaN(index) || !this.videos[index]) {
					return;
				}

				const transferStartRect = card
					.querySelector('.jw-home-video__card-thumbnail')
					?.getBoundingClientRect();

				this.selectVideo(index);

				if (this.isMobileView()) {
					this.openModal(card, index);
				}

				this.animateVideoTransfer(
					transferStartRect,
					this.videos[index]
				);
			});
		});

		this.closeButton?.addEventListener('click', () => {
			this.closeModal();
		});

		document.addEventListener('keydown', (event) => {
			if (
				'Escape' === event.key &&
				this.root.classList.contains('is-modal-open')
			) {
				this.closeModal();
			}
		});

		this.mobileMedia.addEventListener('change', () => {
			this.handleBreakpointChange();
		});

		if ('IntersectionObserver' in window) {
			this.observer = new window.IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						this.handleVisibility(entry.isIntersecting);
					});
				},
				{ threshold: 0.15 }
			);
			this.observer.observe(this.root);
		} else {
			this.handleVisibility(true);
		}
	}

	initCardPreviews() {
		this.cardPreviews.forEach((preview) => {
			preview.defaultMuted = true;
			preview.muted = true;
			preview.loop = true;
			preview.playsInline = true;
			preview.addEventListener('playing', () => {
				preview
					.closest('[data-video-card]')
					?.classList.add('is-preview-playing');
			});
			preview.addEventListener('pause', () => {
				preview
					.closest('[data-video-card]')
					?.classList.remove('is-preview-playing');
			});
		});

		if (!this.cardPreviews.length || this.reduceMotion) {
			return;
		}

		if (!('IntersectionObserver' in window)) {
			this.cardPreviews.forEach((preview) => {
				this.playCardPreview(preview);
			});
			return;
		}

		this.previewObserver = new window.IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (
						entry.isIntersecting &&
						0.35 <= entry.intersectionRatio
					) {
						this.playCardPreview(entry.target);
					} else {
						entry.target.pause();
					}
				});
			},
			{ threshold: [0, 0.35] }
		);

		this.cardPreviews.forEach((preview) => {
			this.previewObserver.observe(preview);
		});
	}

	playCardPreview(preview) {
		if (!preview.src) {
			const source = preview.dataset.videoPreviewSrc || '';

			if (!source) {
				return;
			}

			preview.src = source;
			preview.load();
		}

		preview.play().catch(() => {
			preview
				.closest('[data-video-card]')
				?.classList.remove('is-preview-playing');
		});
	}

	selectVideo(index) {
		this.activeIndex = index;
		const video = this.videos[index];

		this.cards.forEach((card) => {
			const isActive =
				Number.parseInt(card.dataset.videoIndex || '', 10) === index;
			card.hidden = isActive && !this.isMobileView();
			card.classList.toggle('is-active', isActive);
			card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});

		this.poster.src = video.thumbnail;
		this.poster.alt = video.title;
		this.title.textContent = video.title;
		if (this.description) {
			this.description.textContent = video.description;
		}
		if (this.views) {
			this.views.textContent = video.viewsLabel;
		}
		this.duration.textContent = video.durationLabel;
		if (this.published) {
			this.published.textContent = video.publishedLabel;
			this.published.dateTime = video.publishedDateTime;
		}
		this.playButton.setAttribute(
			'aria-label',
			`Phát video: ${video.title}`
		);

		this.resetPlayer();
		this.ensurePlayer(true);
	}

	animateVideoTransfer(startRect, video) {
		if (
			this.isMobileView() ||
			this.reduceMotion ||
			!startRect?.width ||
			!startRect?.height ||
			!video?.thumbnail
		) {
			return;
		}

		window.requestAnimationFrame(() => {
			const endRect = this.stage.getBoundingClientRect();

			if (!endRect.width || !endRect.height) {
				return;
			}

			const transferImage = document.createElement('img');
			transferImage.className = 'jw-home-video__transfer-preview';
			transferImage.src = video.thumbnail;
			transferImage.alt = '';
			transferImage.setAttribute('aria-hidden', 'true');
			document.body.append(transferImage);

			const animation = transferImage.animate(
				[
					{
						top: `${startRect.top}px`,
						left: `${startRect.left}px`,
						width: `${startRect.width}px`,
						height: `${startRect.height}px`,
						opacity: 0.92,
						borderRadius: '12px',
					},
					{
						top: `${endRect.top}px`,
						left: `${endRect.left}px`,
						width: `${endRect.width}px`,
						height: `${endRect.height}px`,
						opacity: 0.12,
						borderRadius: this.isMobileView() ? '0' : '12px',
					},
				],
				{
					duration: 520,
					easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
					fill: 'forwards',
				}
			);

			const finishTransfer = () => {
				transferImage.remove();
				this.stage.classList.remove('is-transfer-arriving');
				window.requestAnimationFrame(() => {
					this.stage.classList.add('is-transfer-arriving');
				});
			};

			animation.addEventListener('finish', finishTransfer, {
				once: true,
			});
			animation.addEventListener('cancel', () => transferImage.remove(), {
				once: true,
			});
		});
	}

	handleVisibility(isVisible) {
		this.isVisible = isVisible;
		this.root.classList.toggle('is-in-view', isVisible);

		if (!isVisible) {
			this.pausePlayer();
			return;
		}

		if (
			this.isMobileView() &&
			!this.root.classList.contains('is-modal-open')
		) {
			return;
		}

		if (!this.reduceMotion) {
			this.ensurePlayer(false);
		}
	}

	isMobileView() {
		return this.mobileMedia.matches;
	}

	syncResponsiveState() {
		this.cards.forEach((card) => {
			const isActive =
				Number.parseInt(card.dataset.videoIndex || '', 10) ===
				this.activeIndex;
			card.hidden = isActive && !this.isMobileView();
		});

		if (
			this.isMobileView() &&
			!this.root.classList.contains('is-modal-open')
		) {
			this.stage.setAttribute('aria-hidden', 'true');
		} else {
			this.stage.removeAttribute('aria-hidden');
		}
	}

	handleBreakpointChange() {
		if (this.root.classList.contains('is-modal-open')) {
			this.closeModal(false);
		} else if (this.isMobileView()) {
			this.resetPlayer();
		}

		this.syncResponsiveState();

		if (!this.isMobileView() && this.isVisible && !this.reduceMotion) {
			this.ensurePlayer(false);
		}
	}

	openModal(trigger, index) {
		if (!this.isMobileView()) {
			return;
		}

		this.lastFocusedElement = trigger;
		this.root.classList.add('is-modal-open');
		document.body.classList.add('jw-home-video-modal-open');
		this.stage.removeAttribute('aria-hidden');
		this.stage.setAttribute('role', 'dialog');
		this.stage.setAttribute('aria-modal', 'true');
		this.stage.setAttribute(
			'aria-label',
			`Video: ${this.videos[index].title}`
		);
		this.closeButton?.focus();
	}

	closeModal(restoreFocus = true) {
		if (!this.root.classList.contains('is-modal-open')) {
			return;
		}

		this.resetPlayer();
		this.root.classList.remove('is-modal-open');
		document.body.classList.remove('jw-home-video-modal-open');
		this.stage.removeAttribute('role');
		this.stage.removeAttribute('aria-modal');
		this.stage.removeAttribute('aria-label');
		this.stage.setAttribute('aria-hidden', 'true');

		if (restoreFocus) {
			this.lastFocusedElement?.focus();
		}

		this.lastFocusedElement = null;
	}

	ensurePlayer(isUserInitiated) {
		if (!this.isVisible && !isUserInitiated) {
			return Promise.resolve();
		}

		if (this.player) {
			this.playPlayer();
			return Promise.resolve();
		}

		if (this.playerPromise) {
			return this.playerPromise;
		}

		const video = this.videos[this.activeIndex];
		const requestId = ++this.playerRequestId;

		this.playerPromise =
			video.sourceType === 'file'
				? this.createNativePlayer(video, requestId)
				: this.createYoutubePlayer(video, requestId, isUserInitiated);

		return this.playerPromise.finally(() => {
			if (requestId === this.playerRequestId) {
				this.playerPromise = null;
			}
		});
	}

	createNativePlayer(video, requestId) {
		const player = document.createElement('video');
		player.className = 'jw-home-video__native-player';
		player.controls = true;
		player.muted = true;
		player.loop = true;
		player.playsInline = true;
		player.preload = 'metadata';
		player.poster = video.thumbnail;
		player.src = video.source;

		this.playerContainer.replaceChildren(player);
		this.player = player;
		this.playerType = 'file';
		this.stage.classList.add('has-player');

		if (requestId !== this.playerRequestId) {
			this.resetPlayer();
			return Promise.resolve();
		}

		return player.play().catch(() => {
			this.stage.classList.remove('has-player');
		});
	}

	createYoutubePlayer(video, requestId, isUserInitiated) {
		return loadYoutubePlayerApi()
			.then(
				(YT) =>
					new Promise((resolve) => {
						if (requestId !== this.playerRequestId) {
							resolve();
							return;
						}

						const mountPoint = document.createElement('div');
						this.playerContainer.replaceChildren(mountPoint);
						this.playerType = 'youtube';
						this.player = new YT.Player(mountPoint, {
							videoId: video.source,
							playerVars: {
								autoplay: 1,
								controls: 1,
								loop: 1,
								playlist: video.source,
								playsinline: 1,
								rel: 0,
							},
							events: {
								onReady: (event) => {
									event.target.mute();
									this.stage.classList.add('has-player');
									if (this.isVisible || isUserInitiated) {
										event.target.playVideo();
									} else {
										event.target.pauseVideo();
									}
									resolve();
								},
								onError: () => {
									this.resetPlayer();
									resolve();
								},
							},
						});
					})
			)
			.catch(() => {
				this.resetPlayer();
			});
	}

	playPlayer() {
		if ('file' === this.playerType) {
			this.player
				.play()
				.then(() => {
					this.stage.classList.add('has-player');
				})
				.catch(() => {
					this.stage.classList.remove('has-player');
				});
			return;
		}

		if ('youtube' === this.playerType && this.player?.playVideo) {
			this.player.mute();
			this.player.playVideo();
		}
	}

	pausePlayer() {
		if ('file' === this.playerType) {
			this.player?.pause();
			return;
		}

		if ('youtube' === this.playerType && this.player?.pauseVideo) {
			this.player.pauseVideo();
		}
	}

	resetPlayer() {
		this.playerRequestId += 1;

		if ('file' === this.playerType && this.player) {
			this.player.pause();
			this.player.removeAttribute('src');
			this.player.load();
		} else if ('youtube' === this.playerType && this.player?.destroy) {
			this.player.destroy();
		}

		this.playerContainer.replaceChildren();
		this.stage.classList.remove('has-player');
		this.player = null;
		this.playerType = '';
		this.playerPromise = null;
	}
}

export class ThemeHomeVideoManager {
	init() {
		queryAll('[data-jw-home-video]').forEach((root) => {
			new HomeVideoController(root).init();
		});
	}
}
